import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { describeApiError } from '@/lib/api-errors';
import i18n from '@/lib/i18n';
import * as designsApi from '@/api/designs';
import type {
  CreateDesignDto,
  UpdateDesignDto,
  DesignListQuery,
  SaveDesignConfigurationDto,
} from '@/api/designs';

// ═══════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════

export const designKeys = {
  all: ['designs'] as const,
  lists: () => [...designKeys.all, 'list'] as const,
  list: (query?: DesignListQuery) => [...designKeys.lists(), query] as const,
  details: () => [...designKeys.all, 'detail'] as const,
  detail: (id: string) => [...designKeys.details(), id] as const,
  versions: (id: string) => [...designKeys.detail(id), 'versions'] as const,
  products: (id: string) => [...designKeys.detail(id), 'products'] as const,
  pricing: (id: string) => [...designKeys.detail(id), 'pricing'] as const,
};

// ═══════════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════════

/**
 * The pricing quote of a design (seller cost, profit, variant surcharges per
 * product). Refetched after every successful save, because the quote reads
 * the stored design state (see designStore.adoptPricingAfterSave).
 */
export function useDesignPricing(id: string | null | undefined) {
  return useQuery({
    queryKey: designKeys.pricing(id ?? ''),
    queryFn: () => designsApi.getDesignPricing(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch all designs with optional filtering
 */
export function useDesigns(query?: DesignListQuery) {
  return useQuery({
    queryKey: designKeys.list(query),
    queryFn: () => designsApi.getDesigns(query),
  });
}

/** Page size of the seller overview; 24 fills a 4-column grid six times. */
export const DESIGN_OVERVIEW_PAGE_SIZE = 24;

/** How often the overview refreshes while a channel job is running. */
export const DESIGN_OVERVIEW_POLL_INTERVAL_MS = 4000;

/**
 * Query of the seller overview: review-queue drafts stay out (they live on
 * the Review page), newest change first, one grid page at a time.
 */
export const DESIGN_OVERVIEW_QUERY: DesignListQuery = {
  excludeReviewQueue: true,
  limit: DESIGN_OVERVIEW_PAGE_SIZE,
  sortBy: 'updatedAt',
  sortOrder: 'desc',
};

/**
 * Hook behind "Mehr laden" on the design overview. Pages are appended
 * until `meta.totalPages` is reached; a failed next page keeps the
 * loaded pages and reports `isFetchNextPageError`.
 */
export function useInfiniteDesigns() {
  return useInfiniteQuery({
    queryKey: designKeys.list(DESIGN_OVERVIEW_QUERY),
    queryFn: ({ pageParam }) =>
      designsApi.getDesigns({ ...DESIGN_OVERVIEW_QUERY, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages
        ? lastPage.meta.page + 1
        : undefined,
    // Channel jobs finish in the background, so the overview refreshes while
    // any row is running and stops the moment none is — the same shape the
    // review queue uses, so an idle overview costs nothing.
    refetchInterval: (query) => {
      const pages = query.state.data?.pages ?? [];
      const running = pages.some((page) =>
        page.data.some((design) =>
          (design.channelPublications ?? []).some(
            (publication) => publication.status === 'RUNNING'
          )
        )
      );
      return running ? DESIGN_OVERVIEW_POLL_INTERVAL_MS : false;
    },
  });
}

/**
 * Hook to fetch a single design by ID
 */
export function useDesign(id: string | undefined) {
  return useQuery({
    queryKey: designKeys.detail(id!),
    queryFn: () => designsApi.getDesign(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch design versions
 */
export function useDesignVersions(id: string | undefined) {
  return useQuery({
    queryKey: designKeys.versions(id!),
    queryFn: () => designsApi.getDesignVersions(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch design products
 */
export function useDesignProducts(id: string | undefined) {
  return useQuery({
    queryKey: designKeys.products(id!),
    queryFn: () => designsApi.getDesignProducts(id!),
    enabled: !!id,
  });
}

// ═══════════════════════════════════════════════════════════════════
// MUTATIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Hook to create a new design
 */
export function useCreateDesign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDesignDto) => designsApi.createDesign(data),
    onSuccess: (design) => {
      queryClient.invalidateQueries({ queryKey: designKeys.lists() });
      toast.success(i18n.t('creator.toast.designCreated'));
      return design;
    },
    onError: (error) => {
      toast.error(
        i18n.t('creator.toast.designCreateFailed', {
          message: describeApiError(error),
        })
      );
    },
  });
}

/**
 * Hook to update a design
 */
export function useUpdateDesign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDesignDto }) =>
      designsApi.updateDesign(id, data),
    onSuccess: (design) => {
      queryClient.invalidateQueries({ queryKey: designKeys.detail(design.id) });
      queryClient.invalidateQueries({ queryKey: designKeys.lists() });
      // Don't show toast for auto-save
    },
    onError: (error) => {
      toast.error(
        i18n.t('creator.toast.designSaveFailed', {
          message: describeApiError(error),
        })
      );
    },
  });
}

/**
 * Hook to delete a design. Feedback is left to the caller, which owns the
 * language of its toasts (the seller overview is multilingual).
 */
export function useDeleteDesign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => designsApi.deleteDesign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: designKeys.lists() });
      queryClient.removeQueries({ queryKey: designKeys.detail(id) });
    },
  });
}

/**
 * Hook to duplicate a design. Feedback is left to the caller, see
 * `useDeleteDesign`.
 */
export function useDuplicateDesign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => designsApi.duplicateDesign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: designKeys.lists() });
    },
  });
}

/**
 * Hook to save complete design configuration
 */
export function useSaveDesignConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      config,
    }: {
      id: string;
      config: SaveDesignConfigurationDto;
    }) => designsApi.saveDesignConfiguration(id, config),
    onSuccess: (design) => {
      queryClient.invalidateQueries({ queryKey: designKeys.detail(design.id) });
      queryClient.invalidateQueries({
        queryKey: designKeys.pricing(design.id),
      });
      queryClient.invalidateQueries({
        queryKey: designKeys.products(design.id),
      });
      toast.success(i18n.t('creator.toast.configurationSaved'));
    },
    onError: (error) => {
      toast.error(
        i18n.t('creator.toast.configurationSaveFailed', {
          message: describeApiError(error),
        })
      );
    },
  });
}

// ═══════════════════════════════════════════════════════════════════
// PRODUCT MUTATIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Hook to add a product to a design
 */
export function useAddProductToDesign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      designId,
      productId,
      isEnabled,
    }: {
      designId: string;
      productId: string;
      isEnabled?: boolean;
    }) => designsApi.addProductToDesign(designId, productId, isEnabled),
    onSuccess: (_, { designId }) => {
      queryClient.invalidateQueries({
        queryKey: designKeys.products(designId),
      });
      queryClient.invalidateQueries({ queryKey: designKeys.detail(designId) });
      toast.success(i18n.t('creator.toast.productAdded'));
    },
    onError: (error) => {
      toast.error(
        i18n.t('creator.toast.productAddFailed', {
          message: describeApiError(error),
        })
      );
    },
  });
}

/**
 * Hook to remove a product from a design
 */
export function useRemoveProductFromDesign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      designId,
      productId,
    }: {
      designId: string;
      productId: string;
    }) => designsApi.removeProductFromDesign(designId, productId),
    onSuccess: (_, { designId }) => {
      queryClient.invalidateQueries({
        queryKey: designKeys.products(designId),
      });
      queryClient.invalidateQueries({ queryKey: designKeys.detail(designId) });
      toast.success(i18n.t('creator.toast.productRemoved'));
    },
    onError: (error) => {
      toast.error(
        i18n.t('creator.toast.productRemoveFailed', {
          message: describeApiError(error),
        })
      );
    },
  });
}

/**
 * Hook to set product colors and prices
 */
export function useSetProductColors() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      designId,
      productId,
      colors,
    }: {
      designId: string;
      productId: string;
      colors: { productColorId: string; sellingPrice: number }[];
    }) => designsApi.setProductColors(designId, productId, colors),
    onSuccess: (_, { designId }) => {
      queryClient.invalidateQueries({
        queryKey: designKeys.products(designId),
      });
    },
    onError: (error) => {
      toast.error(
        i18n.t('creator.toast.colorsUpdateFailed', {
          message: describeApiError(error),
        })
      );
    },
  });
}
